import { isModifierLine } from './modifierPricing'

/**
 * Состав конструктора одной строкой — для гостя (чек, пречек, экран гостя) и кухни.
 *
 * В базе состав — отдельные строки чека (вариант А: суммы, возвраты и фискализация считаются
 * построчно), а гостю «Боул: курица, рис, соус (бесплатно)» — одно блюдо. Функция сворачивает
 * строки-компоненты в строку блюда: сумма блюда = своя + суммы компонентов, под названием
 * появляется состав (`composition`). Данные чека не меняются, только список для показа.
 *
 * Сворачиваем строки блюд с признаком is_constructor, у которых есть живые строки состава и общая
 * ставка НДС: у разных ставок свёрнутая сумма разошлась бы с НДС построчно, такой состав остаётся
 * отдельными строками, как раньше.
 *
 * Модуль чистый (без импортов, кроме соседнего modifierPricing): один и тот же файл лежит в кассе
 * (src/custom/constructorDisplay.js) и в кабинете (src/helpers/constructorDisplay.js) — правишь в обоих.
 */

function isOn(value) {
	return value === true || value === 1 || value === '1' || value === 't' || value === 'true' || value === 'TRUE'
}

function isLive(line) {
	return Number(line?.status ?? 0) >= 0
}

function lineTotal(line) {
	const stored = Number(line.total_price)
	if (Number.isFinite(stored) && stored >= 0) return stored
	return (Number(line.sale_price) || 0) * (Number(line.quantity) || 0)
}

function belongsTo(parent, child) {
	if (parent.transaction_id && child.parent_transaction_id === parent.transaction_id) return true
	return parent.id != null && child.parent_cheque_product_id != null
		&& String(child.parent_cheque_product_id) === String(parent.id)
}

function round2(value) {
	return Math.round(value * 100) / 100
}

/** Блюдо собирается из компонентов (признак is_constructor карточки товара) */
export function isConstructorDish(line) {
	return isOn(line?.is_constructor)
}

/** Комбо — конструктор с фиксированной ценой, где слот выбирается одним вариантом, а дорогой стоит доплату */
export function isComboDish(line) {
	return isConstructorDish(line) && String(line?.constructor_type) === 'combo'
}

/** «Сыр ×2, Соус (бесплатно)» из частей состава */
export function buildCompositionText(parts, kindLabel = () => '') {
	return parts.map((part) => {
		const quantity = Number(part.quantity) || 0
		const count = quantity > 1 ? ` ×${round2(quantity)}` : ''
		const label = kindLabel(part.price_kind)
		return `${part.name}${count}${label ? ` (${label})` : ''}`
	}).join(', ')
}

/**
 * @param {Array} lines строки чека в порядке показа (блюдо, затем его состав)
 * @param {{ nameOf?: (line: object) => string, kindLabel?: (kind: string) => string }} options
 * @returns {Array} те же строки, где состав конструкторов свёрнут в строку блюда
 */
export function composeConstructorLines(lines, options = {}) {
	if (!Array.isArray(lines) || lines.length === 0) return lines

	const nameOf = options.nameOf || ((line) => line.name)
	const kindLabel = options.kindLabel || (() => '')
	const folded = new Set()
	const result = []
	let changed = false

	lines.forEach((line, index) => {
		if (folded.has(index)) return
		if (isModifierLine(line) || !isOn(line.is_constructor) || !isLive(line)) {
			result.push(line)
			return
		}

		const childIndexes = []
		lines.forEach((child, childIndex) => {
			if (childIndex !== index && !folded.has(childIndex) && isModifierLine(child) && isLive(child) && belongsTo(line, child)) {
				childIndexes.push(childIndex)
			}
		})
		const quantity = Number(line.quantity) || 0
		if (childIndexes.length === 0 || quantity <= 0) {
			result.push(line)
			return
		}

		const children = childIndexes.map((childIndex) => lines[childIndex])
		const rates = new Set([line, ...children].map((item) => String(Number(item.vat) || 0)))
		if (rates.size > 1) {
			result.push(line)
			return
		}

		const parts = []
		children.forEach((child) => {
			const kind = child.price_kind || null
			const existing = parts.find((part) => part.product_id === child.product_id && part.price_kind === kind)
			if (existing) {
				existing.quantity += Number(child.quantity) || 0
			} else {
				parts.push({ product_id: child.product_id, name: nameOf(child), quantity: Number(child.quantity) || 0, price_kind: kind })
			}
		})

		const total = [line, ...children].reduce((sum, item) => sum + lineTotal(item), 0)
		const discount = [line, ...children].reduce((sum, item) => sum + (Number(item.discount) || 0), 0)

		childIndexes.forEach((childIndex) => folded.add(childIndex))
		changed = true
		result.push({
			...line,
			// Цена за порцию с составом: тогда «количество × цена = сумма» в строке сходится
			sale_price: round2(total / quantity),
			total_price: round2(total),
			discount,
			composition: { parts, text: buildCompositionText(parts, kindLabel) },
			composed_line_ids: children.map((child) => child.id).filter((id) => id != null),
		})
	})

	return changed ? result : lines
}

/** Живые строки состава блюда в порядке чека — для панели «вид чека» в окне сборки */
export function constructorChildren(lines, parent) {
	if (!Array.isArray(lines) || !parent) return []
	return lines.filter((line) => line !== parent && isModifierLine(line) && isLive(line) && belongsTo(parent, line))
}

/**
 * Итог собираемого блюда по живым строкам чека — для окна сборки и строки блюда в списке:
 * base — само блюдо, extras — его состав, total — сумма, freeUsed — сколько штук состава
 * уже покрыто бесплатным лимитом по группам (group id → штук), includedUsed / includedUsedByProduct —
 * сколько штук уже занято «входит в цену» по группам и по паре «группа:товар».
 */
export function summarizeConstructor(lines, parent) {
	const summary = { base: 0, extras: 0, total: 0, count: 0, freeUsed: {}, includedUsed: {}, includedUsedByProduct: {} }
	if (!Array.isArray(lines) || !parent) return summary

	summary.base = isLive(parent) ? lineTotal(parent) : 0
	lines.forEach((line) => {
		if (line === parent || !isModifierLine(line) || !isLive(line) || !belongsTo(parent, line)) return
		summary.extras += lineTotal(line)
		summary.count += Number(line.quantity) || 0
		const groupId = line.modifier_parent_id
		const quantity = Number(line.quantity) || 0
		if (line.price_kind === 'free') {
			summary.freeUsed[groupId] = (summary.freeUsed[groupId] || 0) + quantity
		}
		if (line.price_kind === 'included') {
			summary.includedUsed[groupId] = (summary.includedUsed[groupId] || 0) + quantity
			const key = `${groupId}:${line.product_id}`
			summary.includedUsedByProduct[key] = (summary.includedUsedByProduct[key] || 0) + quantity
		}
	})
	summary.base = round2(summary.base)
	summary.extras = round2(summary.extras)
	summary.total = round2(summary.base + summary.extras)

	return summary
}

// Кассиру и гостю важно одно: платить за штуку не нужно. Чем это настроено в кабинете — лимитом
// группы или «входит в цену» — видно только в данных (price_kind), для людей это одно слово
function kindText(kind, t) {
	if (kind === 'free' || kind === 'included') return t('price_kind_free')
	return ''
}

/** Состав свёрнутой строки блюда на языке интерфейса; пусто, если строка не сворачивалась */
export function compositionText(item, t) {
	if (!item?.composition?.parts?.length) return ''
	return buildCompositionText(item.composition.parts, (kind) => kindText(kind, t))
}

/** « (бесплатно)» / « (входит в цену)» для несвёрнутой строки состава; у остальных строк пусто */
export function priceKindSuffix(item, t) {
	if (!isModifierLine(item)) return ''
	const label = kindText(item.price_kind, t)
	return label ? ` (${label})` : ''
}
